import Head from "next/head";
import Script from "next/script";
import React from "react";

import { config } from "@/config/environment";
import { useExperience } from "@/context/ExperienceContext";
import { Metadata } from "@/types/Block";

interface RenderMetadataProps extends Metadata {
  renderGoogleRecaptcha?: boolean;
}

export const RenderMetadata: React.FC<RenderMetadataProps> = ({
  title,
  description,
  metaTags,
  favicon,
  renderGoogleRecaptcha = false
}) => {
  const {
    allowIndexing,
    alternates,
    amplitudeKey,
    canonical,
    environment,
    locale,
    thirdPartyTracking
  } = useExperience();

  React.useEffect(() => {
    const scriptIdAttribute = "GoogleRecaptchaScript";

    // Using next/script would not load the library and using next/head
    // would throw a warning about using scripts in next/head, so we just
    // manually load it on the client instead
    if (renderGoogleRecaptcha && !document.getElementById(scriptIdAttribute)) {
      const script = document.createElement("script");
      script.setAttribute("async", "true");
      script.setAttribute("id", "GoogleRecaptchaScript");
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${config.recaptchaSiteKey}`;
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(scriptIdAttribute);

      if (script) {
        script.remove();
      }

      if ("grecaptcha" in window && window.grecaptcha) {
        delete window.grecaptcha;
      }

      const badge = document.querySelector(".grecaptcha-badge");

      if (badge?.parentNode) {
        badge.parentNode.removeChild(badge);
      }
    };
  }, [renderGoogleRecaptcha]);
  const hasMetaOGDescription = React.useMemo(
    () => metaTags?.some((metaTag) => metaTag.property === "og:description"),
    [metaTags]
  );
  const hasMetaOGTitle = React.useMemo(
    () => metaTags?.some((metaTag) => metaTag.property === "og:title"),
    [metaTags]
  );

  return (
    <>
      <Head>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        {canonical && (
          <>
            <link rel="canonical" href={canonical} />
            {alternates?.map(({ hreflang, url }) => (
              <link key={hreflang} rel="alternate" hrefLang={hreflang} href={url} />
            ))}
            <meta property="og:url" content={canonical} />
          </>
        )}
        <meta property="og:type" content="website" />
        {title && !hasMetaOGTitle && <meta property="og:title" content={title} />}
        {description && !hasMetaOGDescription && (
          <meta property="og:description" content={description} />
        )}
        {metaTags?.map((metaTag, i) => (
          <meta key={i} name={metaTag.name} property={metaTag.property} content={metaTag.content} />
        ))}
        {favicon && <link rel="icon" href={favicon} />}
        {allowIndexing === false && <meta name="robots" content="noindex" />}
        {renderGoogleRecaptcha && <style>{`.grecaptcha-badge { visibility: hidden; }`}</style>}
      </Head>
      <Script
        id="ModernaUIVersion"
        dangerouslySetInnerHTML={{
          __html: `window.version = "${process.env.version}";`
        }}
      />
      {environment !== "local" && (
        <Script
          data-amplitude-key={amplitudeKey}
          data-enable-third-party-tracking={thirdPartyTracking}
          data-locale={locale}
          data-z-index={1300}
          defer={true}
          src="https://static.modernatx.com/track.js"
        />
      )}
    </>
  );
};
